import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const testTypes = [
  { value: 'functional', label: 'Functional Test' },
  { value: 'visual', label: 'Visual Inspection' },
  { value: 'electrical', label: 'Electrical Test' },
  { value: 'mechanical', label: 'Mechanical Test' },
  { value: 'thermal', label: 'Thermal Test' },
  { value: 'pressure', label: 'Pressure Test' },
  { value: 'dimensional', label: 'Dimensional Check' },
  { value: 'safety', label: 'Safety Test' },
];

const testResultSchema = z.object({
  work_order_id: z.number({ required_error: 'Please select a work order' }),
  serial_number: z.string().optional(),
  test_type: z.string().min(1, 'Please select a test type'),
  result: z.enum(['pass', 'fail', 'conditional'], { required_error: 'Please select a result' }),
  measured_value: z.string().optional(),
  expected_value: z.string().optional(),
  notes: z.string().optional(),
  tested_by: z.string().min(1, 'Tester name is required'),
});

type TestResultFormData = z.infer<typeof testResultSchema>;

interface WorkOrder {
  id: number;
  wo_number: string;
  product_name: string;
}

interface TestResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrders: WorkOrder[];
  onSubmit: (data: TestResultFormData) => void;
  isLoading?: boolean;
}

export function TestResultDialog({
  open,
  onOpenChange,
  workOrders,
  onSubmit,
  isLoading,
}: TestResultDialogProps) {
  const form = useForm<TestResultFormData>({
    resolver: zodResolver(testResultSchema),
    defaultValues: {
      test_type: '',
      result: undefined,
      measured_value: '',
      expected_value: '',
      notes: '',
      tested_by: '',
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const result = form.watch('result');

  const handleSubmit = (data: TestResultFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Record Test Result</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {/* Work Order Selection */}
          <div className="space-y-2">
            <Label>Work Order *</Label>
            <Select
              value={form.watch('work_order_id')?.toString()}
              onValueChange={(value) => form.setValue('work_order_id', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select work order" />
              </SelectTrigger>
              <SelectContent>
                {workOrders.map((wo) => (
                  <SelectItem key={wo.id} value={wo.id.toString()}>
                    <span className="font-mono">{wo.wo_number}</span>
                    <span className="text-muted-foreground ml-2">- {wo.product_name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.work_order_id && (
              <p className="text-sm text-destructive">
                {form.formState.errors.work_order_id.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Serial Number */}
            <div className="space-y-2">
              <Label>Serial Number (optional)</Label>
              <Input
                {...form.register('serial_number')}
                placeholder="SN-XXXXX"
                className="font-mono"
              />
            </div>

            {/* Test Type */}
            <div className="space-y-2">
              <Label>Test Type *</Label>
              <Select
                value={form.watch('test_type')}
                onValueChange={(value) => form.setValue('test_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {testTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.test_type && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.test_type.message}
                </p>
              )}
            </div>
          </div>

          {/* Test Result Selection */}
          <div className="space-y-2">
            <Label>Result *</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => form.setValue('result', 'pass')}
                className={cn(
                  'flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors',
                  result === 'pass'
                    ? 'bg-green-500 text-white border-green-500'
                    : 'hover:bg-green-50 hover:border-green-200'
                )}
              >
                <CheckCircle className="h-5 w-5" />
                Pass
              </button>
              <button
                type="button"
                onClick={() => form.setValue('result', 'fail')}
                className={cn(
                  'flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors',
                  result === 'fail'
                    ? 'bg-red-500 text-white border-red-500'
                    : 'hover:bg-red-50 hover:border-red-200'
                )}
              >
                <XCircle className="h-5 w-5" />
                Fail
              </button>
              <button
                type="button"
                onClick={() => form.setValue('result', 'conditional')}
                className={cn(
                  'flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors',
                  result === 'conditional'
                    ? 'bg-yellow-500 text-white border-yellow-500'
                    : 'hover:bg-yellow-50 hover:border-yellow-200'
                )}
              >
                <AlertTriangle className="h-5 w-5" />
                Conditional
              </button>
            </div>
            {form.formState.errors.result && (
              <p className="text-sm text-destructive">
                {form.formState.errors.result.message}
              </p>
            )}
          </div>

          {/* Measured vs Expected Values */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Measured Value</Label>
              <Input
                {...form.register('measured_value')}
                placeholder="e.g., 24.5V"
              />
            </div>
            <div className="space-y-2">
              <Label>Expected Value</Label>
              <Input
                {...form.register('expected_value')}
                placeholder="e.g., 24V ± 0.5V"
              />
            </div>
          </div>

          {/* Tested By */}
          <div className="space-y-2">
            <Label>Tested By *</Label>
            <Input
              {...form.register('tested_by')}
              placeholder="Tester name"
            />
            {form.formState.errors.tested_by && (
              <p className="text-sm text-destructive">
                {form.formState.errors.tested_by.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              {...form.register('notes')}
              placeholder="Additional observations or comments..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Record Result'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
